import { useQuery } from '@tanstack/react-query';
import { getPortfolioKpis, getEvmFlags } from '@/lib/evm/dataAccess';
import { isDemoMode, isDemoContractId } from '@/config/demoMode';

export interface EvmPortfolioKpis {
  avgSpi: number | null;
  avgCpi: number | null;
  totalEac: number;
  totalBac: number;
  contractCount: number;
}

/**
 * F9.2-X · Frontend Data Governance.
 * Hook agregado para KPIs portfolio-wide. Consome a SAL exclusivamente
 * (sem chamadas diretas a `emp_motor_flags` / `emp_vw_evm_*`).
 *
 * Mantemos a assinatura antiga (recebe activeClusterIds) para compatibilidade,
 * mas o filtro de cluster é aplicado via join com `emp_contratos` em camada
 * controlada — quem precisa de filtro fino deve passar a lista de contratos
 * já resolvida.
 */
export function useEvmPortfolioKpis(activeClusterIds: string[]) {
  const flags = getEvmFlags();

  return useQuery<EvmPortfolioKpis | null>({
    queryKey: ['evm-portfolio-kpis', activeClusterIds, flags.source, isDemoMode()],
    queryFn: async () => {
      try {
        // Resolução de contratos por cluster fica delegada ao caller real.
        // Aqui buscamos via SAL todos os indicadores (a view já é blindada
        // por RLS por cluster do usuário corrente).
        const rowsAll = await getPortfolioKpis([]);
        const rows = isDemoMode() ? rowsAll.filter(r => isDemoContractId(r.contrato_id)) : rowsAll;
        if (!rows.length) return null;

        // Pega último período por contrato.
        const latestByContract = new Map<string, typeof rows[number]>();
        for (const r of rows) {
          const prev = latestByContract.get(r.contrato_id);
          if (!prev || r.periodo_referencia > prev.periodo_referencia) {
            latestByContract.set(r.contrato_id, r);
          }
        }

        const filtered = Array.from(latestByContract.values());
        if (!filtered.length) return null;

        const totalBac = filtered.reduce((s, r) => s + (r.baseline_bac || 0), 0);
        const avgSpi = totalBac > 0
          ? filtered.reduce((s, r) => s + (r.spi || 0) * (r.baseline_bac || 0), 0) / totalBac
          : null;
        const avgCpi = totalBac > 0
          ? filtered.reduce((s, r) => s + (r.cpi || 0) * (r.baseline_bac || 0), 0) / totalBac
          : null;
        const totalEac = filtered.reduce((s, r) => s + (r.eac || 0), 0);

        console.debug('[EVM-SAL] Portfolio KPIs computed', {
          source: flags.source,
          contractCount: filtered.length,
          totalBac,
          avgSpi,
          avgCpi,
          totalEac,
        });

        return {
          avgSpi: avgSpi !== null ? Math.round(avgSpi * 100) / 100 : null,
          avgCpi: avgCpi !== null ? Math.round(avgCpi * 100) / 100 : null,
          totalEac,
          totalBac,
          contractCount: filtered.length,
        };
      } catch (err) {
        console.warn('[EVM-SAL] Portfolio KPIs exception:', err);
        return null;
      }
    },
    enabled: activeClusterIds.length > 0,
    staleTime: 3 * 60 * 1000,
  });
}
